import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { StarredFields } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { ReactNode } from 'react';
import { Control, SetFieldValue } from 'react-hook-form';
import { PartyType } from 'types/contacts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { IdentityFormValues, Option } from '../identityForm';

export interface IdentityInputsContext {
    control: Control<IdentityFormValues>;
    setValue: SetFieldValue<IdentityFormValues>;
    partyType: PartyType;
    countries: Option[];
    employmentStatuses: Option[];
    /** The items whose blanks the panel reports missing, starred; none on a related person's card. */
    isRequired?: (itemKey: string) => boolean;
}

export function FieldRow({ children }: { children: ReactNode }) {
    return <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">{children}</div>;
}

function CountrySelect({
    name,
    label,
    context,
}: {
    name:
        | 'placeOfBirthCountry'
        | 'identityDocumentCountry'
        | 'identificationAddress.country'
        | 'visitingAddress.country';
    label: string;
    context: IdentityInputsContext;
}) {
    return (
        <SelectInput<IdentityFormValues, Option>
            control={context.control}
            setValue={context.setValue}
            name={name}
            label={label}
            options={context.countries}
            getOptionLabel="label"
            getOptionIdentifier="value"
            clearable
        />
    );
}

function AddressInputs({
    name,
    label,
    context,
    required = false,
}: {
    name: 'identificationAddress' | 'visitingAddress';
    label: string;
    context: IdentityInputsContext;
    required?: boolean;
}) {
    const { control } = context;
    return (
        <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className={cn(FIELD_LABEL_CLASS, 'mb-2 p-0')}>{t(label)}</legend>
            <FieldRow>
                {/* An address counts once it has a street line and a country. */}
                <StarredFields value={required}>
                    <TextInput control={control} name={`${name}.address1`} label="address-form.address-1" />
                </StarredFields>
                <TextInput control={control} name={`${name}.address2`} label="address-form.address-2" />
            </FieldRow>
            <FieldRow>
                <TextInput control={control} name={`${name}.zip`} label="address-form.zip-code" />
                <TextInput control={control} name={`${name}.city`} label="address-form.city" />
            </FieldRow>
            <StarredFields value={required}>
                <CountrySelect name={`${name}.country`} label="address-form.country" context={context} />
            </StarredFields>
        </fieldset>
    );
}

/**
 * The inputs of one identity item: what a client item or a related person's item asks, by its key. A company asks
 * its name and legal form for NAMES and its registration code for PERSONAL_CODE.
 */
export function IdentityItemInputs({ itemKey, context }: { itemKey: string; context: IdentityInputsContext }) {
    const required = context.isRequired?.(itemKey) ?? false;
    const isAddress = itemKey === 'REGISTRATION_ADDRESS' || itemKey === 'HOME_ADDRESS';
    return (
        <StarredFields value={required && !isAddress}>
            <ItemInputs itemKey={itemKey} context={context} required={required} />
        </StarredFields>
    );
}

function ItemInputs({
    itemKey,
    context,
    required,
}: {
    itemKey: string;
    context: IdentityInputsContext;
    required: boolean;
}) {
    const { control, setValue, partyType } = context;
    const isCompany = partyType === PartyType.COMPANY;
    switch (itemKey) {
        case 'NAME_AND_LEGAL_FORM':
        case 'NAMES':
            return isCompany ? (
                <FieldRow>
                    <TextInput
                        control={control}
                        name="officialName"
                        label="client-identification.field.official-name"
                    />
                    <TextInput control={control} name="legalForm" label="client-identification.field.legal-form" />
                </FieldRow>
            ) : (
                <FieldRow>
                    <TextInput control={control} name="firstName" label="client-identification.field.first-name" />
                    <TextInput control={control} name="lastName" label="client-identification.field.last-name" />
                </FieldRow>
            );
        case 'REGISTRATION_CODE':
            return <TextInput control={control} name="regCode" label="client-identification.item.REGISTRATION_CODE" />;
        case 'PERSONAL_CODE':
            return isCompany ? (
                <TextInput control={control} name="regCode" label="client-identification.item.REGISTRATION_CODE" />
            ) : (
                <TextInput control={control} name="personalCode" label="client-identification.item.PERSONAL_CODE" />
            );
        case 'REGISTRATION_ADDRESS':
        case 'HOME_ADDRESS':
            return (
                <AddressInputs
                    name="identificationAddress"
                    label={`client-identification.item.${itemKey}`}
                    context={context}
                    required={required}
                />
            );
        case 'PRINCIPAL_PLACE_OF_BUSINESS':
            return (
                <AddressInputs
                    name="visitingAddress"
                    label="client-identification.item.PRINCIPAL_PLACE_OF_BUSINESS"
                    context={context}
                />
            );
        case 'REGISTRATION_DATE':
            return (
                <DateInput
                    control={control}
                    name="registrationDate"
                    label="client-identification.item.REGISTRATION_DATE"
                />
            );
        case 'REGISTRATION_AUTHORITY':
            return (
                <TextInput
                    control={control}
                    name="registrationAuthority"
                    label="client-identification.item.REGISTRATION_AUTHORITY"
                />
            );
        case 'NATURE_OF_BUSINESS':
            return (
                <TextAreaInput
                    control={control}
                    name="natureOfBusiness"
                    label="client-identification.item.NATURE_OF_BUSINESS"
                    minRows={2}
                />
            );
        case 'DATE_OF_BIRTH':
            return <DateInput control={control} name="dateOfBirth" label="client-identification.item.DATE_OF_BIRTH" />;
        case 'PLACE_OF_BIRTH':
            return (
                <FieldRow>
                    <CountrySelect
                        name="placeOfBirthCountry"
                        label="client-identification.field.place-of-birth-country"
                        context={context}
                    />
                    <TextInput
                        control={control}
                        name="placeOfBirthCity"
                        label="client-identification.field.place-of-birth-city"
                    />
                </FieldRow>
            );
        case 'NATIONALITIES':
            return (
                <MultiSelectInput<IdentityFormValues, Option>
                    control={control}
                    setValue={setValue}
                    name="nationalities"
                    label="client-identification.item.NATIONALITIES"
                    options={context.countries}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                />
            );
        case 'ID_DOCUMENT_NUMBER':
            return (
                <>
                    <FieldRow>
                        <TextInput
                            control={control}
                            name="identityDocumentNumber"
                            label="client-identification.item.ID_DOCUMENT_NUMBER"
                        />
                        <CountrySelect
                            name="identityDocumentCountry"
                            label="client-identification.field.id-document-country"
                            context={context}
                        />
                    </FieldRow>
                    <DateInput
                        control={control}
                        name="identityDocumentExpiry"
                        label="client-identification.field.id-document-expiry"
                    />
                </>
            );
        case 'OCCUPATION':
            return (
                <FieldRow>
                    <TextInput control={control} name="occupation" label="client-identification.item.OCCUPATION" />
                    <StarredFields value={false}>
                        <SelectInput<IdentityFormValues, Option>
                            control={control}
                            setValue={setValue}
                            name="employmentStatus"
                            label="client-identification.field.employment-status"
                            options={context.employmentStatuses}
                            getOptionLabel="label"
                            getOptionIdentifier="value"
                            clearable
                        />
                    </StarredFields>
                </FieldRow>
            );
        default:
            return null;
    }
}
